<template>
  <mdb-container>
    <div v-if="currentGroup">
      <banner-small :group="currentGroup" title="Discussion Forum"/>
    </div>

    <div v-if="showSummary && details.groupId">
      <div v-if="details.forums.length==0 && !usingDiscourse">
        <mdb-card class="text-left">
          <mdb-card-body>
            No discussion forums have been set up for this group yet.
            <mdb-icon fal icon="sad-tear"/>
          </mdb-card-body>
        </mdb-card>
      </div>

      <div v-if="usingDiscourse">
        <mdb-card class="text-left">
          <mdb-card-body>
            <div>
              {{ currentGroup.name }} is using an external Forum tool called <i>Discourse</i>. Hit the button below to
              access this service.
            </div>
            <mdb-btn @click="openDiscourse">Access Forum Now</mdb-btn>
          </mdb-card-body>
        </mdb-card>
      </div>

      <mdb-row>
        <mdb-col class="text-left" md="7" lg="7" xl="7">
          <mdb-list-group class="text-left" v-if="details.forums && details.forums.length > 0">
            <h4>Forum List</h4>
            <mdb-list-group-item :color="i%2 ? 'secondary' : 'primary'" :key="forum.id" class="mb-4"
                                 v-for="(forum,i) in details.forums">
              <a @click="viewForum(forum)">{{ forum.title }}</a>
              <mdb-badge pill v-if="forum.authorizations.length > 0 && forum.discussions > 0">{{ forum.discussions }}
                {{ forum.discussions.length == 1 ? 'topic' : 'topics' }}
              </mdb-badge>
              <div class="text-danger" v-if="forum.authorizations.length==0">
                <small>
                  <mdb-icon icon="exclamation"/>&nbsp; Set Access</small>
              </div>
            </mdb-list-group-item>
          </mdb-list-group>
          <div class="mb-4 text-left" v-if="currentGroup && currentGroup.role=='Admin' && !usingDiscourse">
            <mdb-btn @click="createForum" icon="plus-circle" rounded size="sm">New Forum</mdb-btn>
          </div>
        </mdb-col>

        <mdb-col class="text-left" md="5" lg="5" xl="5" v-if="groupRecent && groupRecent.length > 0">
          <h4>Latest Activity</h4>
          <mdb-list-group class="text-left" v-if="groupRecent && groupRecent.length > 0">
            <mdb-list-group-item :color="i%2 ? 'secondary' : 'primary'" :justifyContentBetween="false" :key="i"
                                 class="mb-4"
                                 style="min-height:100px;" v-for="(msg,i) in groupRecent">
              <user-avatar :avatar="msg.member.avatar" :id="msg.member.id" :memberId="msg.member.id"
                           :name="msg.member.name"
                           :reputation="msg.member.reputation" :size="50"/>
              <div>
                <a @click="viewDiscussion(msg)">{{ msg.text }} <i>"{{ msg.subject }}"</i> on the forum
                  <i>{{ msg.title }}</i>.</a>
                <small>
                  <mdb-icon icon="clock"/>
                  {{ msg.date }}</small>
              </div>

            </mdb-list-group-item>
          </mdb-list-group>

        </mdb-col>

      </mdb-row>

    </div>

    <mdb-card class="mb-4" v-if="showForum">
      <mdb-card-header>
        <div class="h4 text-left">{{ currentForum.title }}
          <div style="float:right" v-if="currentGroup && currentGroup.role=='Admin'">
            <mdb-btn @click="access()" icon="shield" rounded size="sm">Set Access</mdb-btn>
            <mdb-btn @click="editForum(currentForum)" icon="pencil" rounded size="sm">Edit</mdb-btn>
            <mdb-btn @click="deleteForum(currentForum)" color="danger" icon="trash" rounded size="sm">Delete</mdb-btn>
          </div>
        </div>
      </mdb-card-header>
      <mdb-card-body class="text-left">
        <div v-html="currentForum.description"/>
        <div class="text-danger" v-if="currentForum.authorizations.length==0">
          <small>No access set up for this forum. Nobody will be able to see this except group admins.</small>
        </div>
        <mdb-btn @click="hideForum" icon="arrow-left" rounded size="sm">Back to Forum List</mdb-btn>
        <mdb-btn @click="newTopic" icon="comment-plus" rounded size="sm" v-if="authorizations && authorizations.post">
          New Topic
        </mdb-btn>
        <mdb-btn @click="showShareLink = !showShareLink" icon="share-alt" rounded size="sm">{{
            showShareLink ? 'Hide' :
              'Get'
          }} Forum Link
        </mdb-btn>
        <div v-if="showShareLink">
          <p><b>Shareable URL</b>: <input id="shareable-url" v-model="currentForum.url"/>
            <mdb-btn @click="copyClipboard()" icon="copy" rounded size="sm">Copy to Clipboard</mdb-btn>
          </p>
        </div>

        <mdb-row>
          <mdb-col class="text-left" md="7" lg="7" xl="7">
            <h4>All Topics</h4>
            <div v-if="topics.length==0 && !executing">
              Currently No Topics
            </div>
            <div v-else class="text-info small">
              Topics sorted by {{ sortField }}.
              <a href="" size="sm" @click.prevent="toggleSortField">Sort by
                {{ sortField == 'subject' ? 'date' : 'subject' }}</a>.
              Order is {{ sortOrder == "asc" ? "oldest to newest" : "newest to oldest" }}.
              <a href="" size="sm" @click.prevent="toggleSortOrder">Toggle Order</a>.
            </div>
            <mdb-list-group class="text-left" v-if="topics && topics.length > 0">
              <mdb-list-group-item :color="i%2 ? 'secondary' : 'primary'" :justifyContentBetween="false" :key="i"
                                   class="mb-4"
                                   style="min-height:100px;"
                                   v-for="(topic,i) in topics">
                <user-avatar :avatar="topic.member.avatar" :id="topic.member.id" :memberId="topic.member.id"
                             :name="topic.member.name"
                             :reputation="topic.member.reputation" :size="50"/>
                <div>
                  <a @click="viewTopic(topic)">{{ topic.subject }}</a>
                  <small>
                    <mdb-icon icon="clock"/>
                    {{ topic.stats.lastActivity }}</small>
                </div>

              </mdb-list-group-item>
            </mdb-list-group>
          </mdb-col>

          <mdb-col class="text-left" md="5" lg="5" xl="5">
            <h4>Latest Activity</h4>
            <div v-if="recent.length==0 && !executing">
              Nothing to show
            </div>
            <mdb-list-group class="text-left" v-if="recent && recent.length > 0">
              <mdb-list-group-item :color="i%2 ? 'secondary' : 'primary'" :justifyContentBetween="false" :key="i"
                                   class="mb-4"
                                   style="min-height:100px;" v-for="(msg,i) in recent">
                <user-avatar :avatar="msg.member.avatar" :id="msg.member.id" :memberId="msg.member.id"
                             :name="msg.member.name"
                             :reputation="msg.member.reputation" :size="50"/>
                <div>
                  <a @click="viewDiscussion(msg)">{{ msg.text }} <i>"{{ msg.subject }}"</i>.</a>
                  <small>
                    <mdb-icon icon="clock"/>
                    {{ msg.date }}</small>
                </div>
              </mdb-list-group-item>
            </mdb-list-group>

          </mdb-col>

        </mdb-row>
      </mdb-card-body>
    </mdb-card>
    <div class="mb-4" v-if="showTopic">
      <messaging :header-title="currentForum.title"
                 :id="currentForum.id"
                 :labels="messagingLabels"
                 :topic="currentTopic.id"
                 @closeTopic="closeTopic"
                 type="forum"
      />
    </div>

    <mdb-modal v-if="followHintModal && showTopic" @close="followHintModal = false" class="animated" info
               position="top-left"
               removeBackdrop side>
      <mdb-modal-header>
        <p class="heading">Hint</p>
      </mdb-modal-header>
      <mdb-modal-body>
        <p class="text-left">
          If you wish to be notified about new messages on a topic, be sure to click the "Follow Topic"
          button. You can always toggle it off later if you no longer wish to be notified when a topic
          is updated.
        </p>
        <mdb-btn @click="stopHint" color="primary" size="sm">Don't show me this anymore.</mdb-btn>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal @close="editModal = false" size="lg" v-if="editModal">
      <mdb-modal-header :close="true" class="secondary-color">Forum Details
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-input label="Title" placeholder="Title" type="text" v-model="working.title"/>
        <h5>Description of Forum</h5>
        <editor :init="editorConfig" label="Description" v-if="!executing" v-model="working.description"/>
        <mdb-btn :disabled="!working.description || !working.title" @click="doSave" color="success">
          <mdb-icon icon="save"/>
          Save
        </mdb-btn>
        <mdb-btn @click="editModal = false" color="danger">
          <mdb-icon icon="window-close"/>
          Cancel
        </mdb-btn>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-card class="mb-4" v-if="showAccess">
      <mdb-card-header><h4>{{ currentForum.title }} Access</h4></mdb-card-header>
      <mdb-card-body class="text-left">
        <div><small>Note: Group admins have full access to all forums & features.</small></div>
        <mdb-btn @click="hideAccess" icon="arrow-left" rounded size="sm">Back to Forum</mdb-btn>
        <mdb-btn @click="addAuth" icon="plus-circle" rounded size="sm">Add Access</mdb-btn>

        <mdb-tbl responsive striped>
          <mdb-tbl-head class="secondary-color">
            <tr>
              <th>Type</th>
              <th>Role/Member</th>
              <th>Moderate</th>
              <th>New Topic</th>
              <th>Add Replies</th>
              <th></th>
            </tr>
          </mdb-tbl-head>
          <mdb-tbl-body>
            <tr :key="i" v-for="(auth,i) in currentForum.authorizations">
              <td>{{ auth.type }}</td>
              <td v-if="auth.type=='Group Role'">{{ auth.role == "Organizer" ? "Group Organizers" : auth.role }}</td>
              <td v-if="auth.type=='Specific Member'">{{ auth.member.name }}</td>
              <td>
                <mdb-icon icon="check" v-if="auth.mayModerate"/>
              </td>
              <td>
                <mdb-icon icon="check" v-if="auth.mayPostNew"/>
              </td>
              <td>
                <mdb-icon icon="check" v-if="auth.mayReply"/>
              </td>
              <td>
                <mdb-btn @click="editAuth(auth)" floating icon="pencil" size="sm"
                         tag="a"
                         type=""/>
                <mdb-btn @click="deleteAuth(auth)" color="danger" floating icon="trash"
                         size="sm"
                         tag="a" type=""/>
              </td>
            </tr>
          </mdb-tbl-body>
        </mdb-tbl>

      </mdb-card-body>
    </mdb-card>

    <mdb-modal @close="accessModal = false" size="lg" v-if="accessModal">
      <mdb-modal-header :close="true" class="secondary-color">Access
      </mdb-modal-header>
      <mdb-modal-body class="text-left">
        <div>
          <label for="access-type">Access Type</label>
          <select class="browser-default custom-select" id="access-type" v-model="working.type">
            <option value="Group Role">Access to a Group Role</option>
            <option value="Specific Member">Access to a Specific Member</option>
          </select>
        </div>
        <div v-if="working.type=='Group Role'">
          <label for="access-role">Role</label>
          <select class="browser-default custom-select" id="access-role" v-model="working.role">
            <option value="Organizer">Group Organizers</option>
            <option value="All Organizers">All Organizers</option>
            <option value="Member">All Members</option>
          </select>
        </div>
        <div v-if="working.type=='Specific Member'">
          <div v-if="working.member.name">
            <mdb-input disabled label="Member" type="text" v-model="working.member.name">
              <span class="input-group-text md-addon" slot="append">
                <mdb-btn @click="removeMember" color="danger" floating icon="times-circle"
                         size="sm"
                         tag="a" type=""/>
              </span>
            </mdb-input>
          </div>
          <div v-if="!working.member.name">
            <member-lookup @selectedMember="selectedMember"/>
          </div>
        </div>

        <div>
          <input aria-label="Moderate?" class="form-control form-check-input" id="mayModerate" type="checkbox"
                 v-model="working.mayModerate"/>
          <label for="mayModerate">Moderate</label>
        </div>
        <div>
          <input aria-label="Post New Topics" class="form-control form-check-input" id="mayPostNew" type="checkbox"
                 v-model="working.mayPostNew"/>
          <label for="mayPostNew">Post New Topics</label>
        </div>
        <div>
          <input aria-label="Reply to Existing" class="form-control form-check-input" id="mayReply" type="checkbox"
                 v-model="working.mayReply"/>
          <label for="mayReply">Reply to Existing Topics</label>
        </div>

        <mdb-btn :disabled="(working.type=='Specific Member'&&!working.member.name) ||
                            (working.type=='Group Role' && !working.role)" @click="doSaveAccess" color="success">
          <mdb-icon icon="save"/>
          Save
        </mdb-btn>
        <mdb-btn @click="accessModal = false" color="danger">
          <mdb-icon icon="window-close"/>
          Cancel
        </mdb-btn>
      </mdb-modal-body>
    </mdb-modal>

    <yes-no-modal @no="deleteForumModal=false" @yes="doDeleteForum"
                  additional-text="All topics and replies will be removed. This cannot be undone."
                  question="Delete forum?" v-if="deleteForumModal"/>
    <yes-no-modal @no="deleteAccessModal=false" @yes="doDeleteAuth" question="Delete access?" v-if="deleteAccessModal"/>

    <message-create :requireSubject="true" @cancel="showCreateTopic=false"
                    @save="postTopic"
                    label="New Topic"
                    button-label="Save Topic"
                    body-placeholder="Summarize this topic..."
                    v-if="showCreateTopic"
    />

  </mdb-container>

</template>
<script>
import api from '../../services/groupsPlaceApi.js'
import bannerSmall from '../group/BannerSmall'
import {mapState} from 'vuex'
import editor from '@tinymce/tinymce-vue'
import YesNoModal from "../common/YesNoModal";
import UserAvatar from "../common/Avatar";
import {
  mdbBadge,
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardHeader,
  mdbChipInput,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbListGroup,
  mdbListGroupItem,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbRow,
  mdbTbl,
  mdbTblBody,
  mdbTblHead
} from 'mdbvue';
import gpSelect from "../common/GPSelect";
import messaging from "../common/Messaging";
import MemberLookup from "../event/MemberLookup";
import MessageCreate from "../common/MessageCreate";

export default {
  name: "group-forum",
  components: {
    mdbContainer,
    mdbBtn,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardFooter,
    mdbIcon,
    mdbInput,
    mdbRow,
    mdbTbl,
    mdbTblHead,
    mdbTblBody,
    mdbCol,
    mdbChipInput,
    mdbListGroup,
    mdbListGroupItem,
    bannerSmall,
    gpSelect,
    messaging,
    mdbBadge,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    editor,
    YesNoModal,
    MemberLookup,
    MessageCreate,
    UserAvatar
  },
  computed: mapState({
                       currentGroup: state => state.currentGroup,
                       executing: state => state.executing
                     }),
  data() {
    return {
      details: {forums: [], groupId: null},
      showSummary: true,
      showForum: false,
      showAccess: false,
      showTopic: false,
      currentForum: null,
      currentTopic: null,
      editModal: false,
      accessModal: false,
      working: null,
      deleteCandidate: null,
      deleteForumModal: false,
      deleteAccessModal: false,
      currentAuth: null,
      topics: [],
      groupRecent: [],
      recent: [],
      authorizations: null,
      showCreateTopic: false,
      showShareLink: false,
      followHintModal: false,
      sortField: null,
      sortOrder: null,
      usingDiscourse: false,
      discourseEndpoint: null,
      editorConfig: {
        browser_spellcheck: true,
        branding: false,
        plugins: ["image", "link", "code", "paste", "table", "lists"],
        toolbar: ["undo redo cut copy paste bold italic underline numlist bullist fontsizeselect | alignleft alignright aligncenter | styleselect | link unlink code "],
        style_formats_merge: true,
        paste_as_text: true,
        paste_data_images: false,
        relative_urls: false,
        remove_script_host: false,
        height: 400
      },
      messagingLabels: {type: "Comment", pluralType: "Comments", buttonLabel: "Submit"}
    }
  },
  mounted() {
    document.title = "Forum"
    this.$store.dispatch("setExecuting", true)
    api.get("/forum/list/" + this.$route.params.id).then(
      (results) => {
        this.details.groupId = results.data.groupId;
        this.details.forums = results.data.forums;
        this.followHintModal = results.data.showFollowHint;
        this.groupRecent = results.data.recent;
        this.usingDiscourse = results.data.usingDiscourse;
        this.discourseEndpoint = results.data.discourseEndpoint;
        if (!this.currentGroup || this.currentGroup.id != this.details.groupId) {
          api.groupBasics(this.details.groupId, "member");
        }
        if (this.$route.params.forumId) {
          for (let i = 0; i < this.details.forums.length; i++) {
            let f = this.details.forums[i]
            if (f.id == this.$route.params.forumId) {
              this.viewForum(f)
            }
          }
        }
      },
      (error) => {
        if (error.response.status == 403 || error.response.status == 401) {
          this.$router.push({path: '/403', query: {redirect: this.$route.fullPath}})
          return
        }
        this.$notify.error({message: error.response.data, timeOut: 20000})
      }).then(() => {
      this.$store.dispatch("setExecuting", false)
    })
  },
  methods: {
    openDiscourse() {
      window.location = this.discourseEndpoint + "/session/sso"
    },
    copyClipboard() {
      let el = document.getElementById("shareable-url")
      if (!el) return
      el.select()
      el.setSelectionRange(0, 99999);
      document.execCommand("copy")
      this.$notify.success({message: "Copied", timeOut: 1000})
    },
    selectedMember(m) {
      this.working.member = m
    },
    removeMember() {
      this.working.member = {id: null, name: null}
    },
    deleteAuth(a) {
      this.deleteCandidate = a
      this.deleteAccessModal = true
    },
    addAuth() {
      this.currentAuth = null
      this.working = {
        type: "Group Role",
        role: "",
        mayModerate: false,
        mayPostNew: false,
        mayReply: false,
        member: {id: null, name: null}
      }
      this.accessModal = true

    },
    editAuth(auth) {
      this.currentAuth = auth
      this.working = JSON.parse(JSON.stringify(auth));
      this.accessModal = true
    },
    deleteForum(f) {
      this.deleteCandidate = f
      this.deleteForumModal = true
    },
    doDeleteForum() {
      this.deleteForumModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/forum/delete/" + this.deleteCandidate.id).then(
        (results) => {
          this.currentForum = null
          this.showForum = false
          this.showSummary = true
          let loc = this.details.forums.indexOf(this.deleteCandidate)
          this.details.forums.splice(loc, 1)
          this.deleteCandidate = null
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    doDeleteAuth() {
      this.deleteAccessModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/forum/deleteAuth/" + this.deleteCandidate.id).then(
        (results) => {
          let loc = this.currentForum.authorizations.indexOf(this.deleteCandidate)
          this.currentForum.authorizations.splice(loc, 1)
          this.deleteCandidate = null
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    doSave() {
      this.editModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/forum/save/" + this.$route.params.id, this.working).then(
        (results) => {
          if (this.working.id) {
            this.currentForum.title = this.working.title
            this.currentForum.description = this.working.description
          } else {
            this.details.forums.unshift(results.data)
            this.currentForum = this.details.forums[0]
            this.showSummary = false
            this.showForum = true
            this.loadTopics(results.data.id)
          }
        },
        (error) => {
          this.editModal = true
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    doSaveAccess() {
      this.accessModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/forum/auth/" + this.currentForum.id, this.working).then(
        (results) => {
          if (this.working.id) {
            let loc = this.currentForum.authorizations.indexOf(this.currentAuth)
            Object.assign(this.currentForum.authorizations[loc], results.data)

          } else {
            this.currentForum.authorizations.unshift(results.data)
          }
        },
        (error) => {
          this.accessModal = true
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    access() {
      this.showAccess = true
      this.showForum = false
    },
    hideAccess() {
      this.showForum = true
      this.showAccess = false
    },
    editForum(forum) {
      this.currentForum = forum
      this.working = {id: forum.id, title: forum.title, description: forum.description}
      this.editModal = true
    },
    createForum(forum) {
      this.currentForum = null
      this.working = {id: null, title: "", description: ""}
      this.editModal = true
    },
    viewForum(forum) {
      this.currentForum = forum
      this.showSummary = false
      this.showForum = true
      this.loadTopics(this.$route.query.topic)
    },
    hideForum() {
      if (this.$route.params.forumId) {
        this.$router.push("/group/" + this.$route.params.id + "/forum")
      }
      this.showForum = false
      this.showAccess = false
      this.showSummary = true
      this.currentForum = null
    },
    viewTopic(topic) {
      this.currentTopic = topic
      this.showTopic = true
      this.showForum = false
    },
    viewDiscussion(message) {
      this.$router.push("/group/" + this.currentGroup.vanityPath + "/forum/" + message.forumId + "?topic=" + message.topic + "&highlight=" + message.messageId)
    },
    closeTopic() {
      this.showForum = true
      this.showTopic = false
      this.currentTopic = null
      this.$router.replace("/group/" + this.currentGroup.vanityPath + "/forum/" + this.currentForum.id)
    },
    newTopic() {
      this.showCreateTopic = true
    },
    postTopic(msg) {
      this.$store.dispatch("setExecuting", true)
      let url = "/message/forum/" + this.currentForum.id + "/create"
      api.post(url, {messageText: msg.text, subject: msg.subject, postAsAdmin: msg.postAsAdmin}).then(
        (results) => {
          this.$router.push("/group/" + this.currentGroup.vanityPath + "/forum/" + this.currentForum.id + "?topic=" + results.data.message.id)
        },
        (error) => {
          this.$notify.error({message: "Could not post comment. " + error.response.data, timeOut: 20000})
          this.$store.dispatch("setExecuting", false)
        })
    },
    loadTopics(topic) {
      this.sortField = window.localStorage.getItem("forumSortField")
      this.sortOrder = window.localStorage.getItem("forumSortOrder")
      if (!this.sortField) {
        this.sortField = "subject"
        window.localStorage.setItem("forumSortField", this.sortField)
      }
      if (!this.sortOrder) {
        this.sortOrder = "asc"
        window.localStorage.setItem("forumSortOrder", this.sortOrder)
      }
      this.topics = []
      this.$store.dispatch("setExecuting", true)
      api.get("/forum/topics/" + this.currentForum.id + "?sortField=" + this.sortField + "&sortOrder=" + this.sortOrder).then(
        (results) => {
          this.topics = results.data.topics
          this.authorizations = results.data.authorizations
          this.recent = results.data.recent
          if (topic) {
            for (let i = 0; i < this.topics.length; i++) {
              let t = this.topics[i]
              if (t.id == topic) {
                this.viewTopic(t)
              }
            }
          }

        },
        (error) => {
          if (error.response.status == 403 || error.response.status == 401) {
            this.$router.push({path: '/403', query: {redirect: this.$route.fullPath}})
            return
          }
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    toggleSortField: function () {
      if (this.sortField == "subject") this.sortField = "date"
      else this.sortField = "subject"
      window.localStorage.setItem("forumSortField", this.sortField)
      this.loadTopics(null)

    },
    toggleSortOrder: function () {
      if (this.sortOrder == "asc") this.sortOrder = "desc"
      else this.sortOrder = "asc"
      window.localStorage.setItem("forumSortOrder", this.sortOrder)
      this.loadTopics(null)
    },
    stopHint: function () {
      this.followHintModal = false
      this.$store.dispatch("setExecuting", true)
      this.error = null
      api.get("/user/rememberAck/forum-topic-follow-hint", {}).then(
        (details) => {
          //not expecting anything except a 200
        },
        (error) => {
          this.error = error
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    }
  }
};

</script>
